import React, { useMemo } from 'react';

import {
  Button,
  Col,
  List,
  Popover,
  Row,
  Space,
  Typography,
  Tooltip,
} from 'antd';

import {
  CloseOutlined,
  QuestionCircleTwoTone,
  ImportOutlined,
} from '@ant-design/icons';

import { FullWidthSpace } from 'common/components';

import { RequiredActiveProductRoot } from 'common/components/product-add/make-product-active/RequiredActiveProduct';
import {
  ProductsActiveValidating,
  ProductsActiveValidatingModal,
} from './ProductsActiveValidating';

import { useAllMinimumRequirementProductFields } from 'hooks/entityHooks';
import { useRequiredActiveProductModal } from 'common/components/product-add/make-product-active/hooks';

import { additionalRequirementFields } from 'common/components/product-add/make-product-active/constants';

const { Title } = Typography;

export const MissingRequirementProductMessage = ({
  minimumRequirements,
  entityId,
  entityType,
  onRevalidateActiveProduct,
}) => {
  const { flatRequiredProductFields } = useAllMinimumRequirementProductFields();

  const additionalFields = Object.keys(additionalRequirementFields);

  const {
    visibleModalRequiredActiveProduct,
    onModalRequiredActiveProduct,
    offModalRequiredActiveProduct,
  } = useRequiredActiveProductModal();

  const requiredFieldsMapping = useMemo(
    () =>
      minimumRequirements?.errorFields
        ?.map((item) => {
          return flatRequiredProductFields[`${item}`];
        })
        .filter(Boolean), // filter additional fields
    [flatRequiredProductFields, minimumRequirements?.errorFields]
  );

  const additionalRequiredErrorFields = minimumRequirements?.errorFields.filter(
    (field) => additionalFields.includes(field)
  );

  const additionalRequiredFieldsMapping = additionalRequiredErrorFields.map(
    (field) => ({
      fullPathDisplayName: additionalRequirementFields[field].fieldDisplayName,
    })
  );

  return (
    <>
      <Space>
        <Typography.Text type='danger' style={{ marginBottom: 0 }} strong>
          Missing requirements
        </Typography.Text>
        <Tooltip title='Update values'>
          <Button
            type='text'
            size='small'
            icon={<ImportOutlined />}
            onClick={onModalRequiredActiveProduct}
          />
        </Tooltip>
        <Popover
          placement='right'
          content={() => (
            <ValidatingInfo
              requiredErrorFields={requiredFieldsMapping}
              additionalErrorFields={additionalRequiredFieldsMapping}
            />
          )}
        >
          <QuestionCircleTwoTone twoToneColor='#d93026' />
        </Popover>
      </Space>
      {visibleModalRequiredActiveProduct && (
        <RequiredActiveProductRoot>
          <ProductsActiveValidatingModal
            visible={visibleModalRequiredActiveProduct}
            onCancel={offModalRequiredActiveProduct}
            entityId={entityId}
            onRevalidateActiveProduct={onRevalidateActiveProduct}
          >
            <ProductsActiveValidating
              minimumRequirements={minimumRequirements}
              entityType={entityType}
            />
          </ProductsActiveValidatingModal>
        </RequiredActiveProductRoot>
      )}
    </>
  );
};

export const ValidatingInfo = ({
  requiredErrorFields,
  additionalErrorFields,
}) => {
  return (
    <FullWidthSpace
      className='scroller--y'
      style={{
        width: 300,
        maxHeight: 350,
        padding: '0px 8px',
        margin: '0px -8px',
      }}
    >
      <div>
        <Title level={5}>Properties Requirement</Title>
        <ErrorList errorFields={requiredErrorFields} />
      </div>
      <div>
        <Title level={5}>Additional Requirement</Title>
        <ErrorList errorFields={additionalErrorFields} />
      </div>
    </FullWidthSpace>
  );
};

const ErrorList = ({ errorFields }) => {
  return (
    <List
      dataSource={errorFields}
      renderItem={(item) => {
        return (
          <Row style={{ marginBottom: 4 }} gutter={16} wrap={false}>
            <Col flex={1}>
              <Typography.Text
                style={{
                  paddingLeft: 8,
                  lineHeight: 'normal',
                  display: 'inline-block',
                }}
              >
                {item.fullPathDisplayName}
              </Typography.Text>
            </Col>
            <Col flex={0}>
              <CloseOutlined style={{ color: '#cf1322', margin: '4px 0px' }} />
            </Col>
          </Row>
        );
      }}
    />
  );
};
